import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Activity, Menu, Bell, Search, LogOut, User, Settings, Check, Home } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useCurrentUser } from "@/components/auth"
import { UserProfileDialog } from "@/components/user"
import { HelpDrawer } from "@/components/help"
import { MeetCallsButton } from "@/components/layout/MeetCallsButton"
import { AppSwitcher } from "@/components/layout/AppSwitcher"
import { api } from "@/services/api"

interface HeaderProps {
  onToggleSidebar?: () => void
  onOpenPalette?: () => void
}

function timeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return "gerade eben"
  if (diffMin < 60) return `vor ${diffMin} Min.`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `vor ${diffH} Std.`
  const diffD = Math.floor(diffH / 24)
  return `vor ${diffD} Tag${diffD > 1 ? "en" : ""}`
}

function getLoadClasses(status?: "low" | "medium" | "high" | "critical") {
  switch (status) {
    case "critical":
      return "border-red-300 bg-red-50 text-red-700"
    case "high":
      return "border-amber-300 bg-amber-50 text-amber-700"
    case "medium":
      return "border-sky-300 bg-sky-50 text-sky-700"
    case "low":
      return "border-emerald-300 bg-emerald-50 text-emerald-700"
    default:
      return "border-border bg-secondary/50 text-muted-foreground"
  }
}

export function Header({ onToggleSidebar, onOpenPalette }: HeaderProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [profileDialogOpen, setProfileDialogOpen] = useState(false)
  const { profile, logout, isAuthenticated } = useCurrentUser()
  const [isMac, setIsMac] = useState(false)
  useEffect(() => {
    setIsMac(typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform))
  }, [])

  useEffect(() => {
    const profileTarget = searchParams.get("profile")
    if (profileTarget === "google" || profileTarget === "integrations") {
      setProfileDialogOpen(true)
    }
  }, [searchParams])

  // Notifications
  const { data: notificationCount } = useQuery({
    queryKey: ["notifications", "count"],
    queryFn: () => api.notifications.count(),
    refetchInterval: 30000,
  })

  const { data: recentNotifications } = useQuery({
    queryKey: ["notifications", "recent"],
    queryFn: () => api.notifications.list({ limit: 10 }),
    refetchInterval: 30000,
  })

  const { data: serverLoad } = useQuery({
    queryKey: ["server-load"],
    queryFn: () => api.getServerLoad(),
    refetchInterval: 15000,
    staleTime: 10000,
    retry: 1,
  })

  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.notifications.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => api.notifications.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
  })

  const unreadCount = (notificationCount as any)?.unreadCount ?? 0
  const notifications: any[] = Array.isArray(recentNotifications) ? recentNotifications : []

  // Benutzerinformationen aus dem Profil
  const userName = profile?.name || "Benutzer"
  const userEmail = profile?.email || ""
  const userAvatar = profile?.picture

  const initials = userName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U"

  return (
    <header className="flex h-16 items-center justify-between border-b bg-card px-4 gap-4">
      {/* Left: Menu Toggle & Search */}
      <div className="flex items-center gap-4 flex-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleSidebar}
          className="shrink-0"
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle Sidebar</span>
        </Button>

        <button
          type="button"
          onClick={onOpenPalette}
          className="relative flex-1 max-w-md flex items-center gap-2 rounded-md border bg-secondary/50 px-3 py-1.5 text-sm text-muted-foreground hover:bg-secondary transition-colors text-left"
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">Suchen oder navigieren...</span>
          <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] font-mono bg-background px-1.5 py-0.5 rounded border">
            {isMac ? "⌘" : "Ctrl"} K
          </kbd>
        </button>

        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`hidden md:flex h-9 min-w-24 items-center gap-2 rounded-md border px-3 text-xs font-medium ${getLoadClasses(serverLoad?.status)}`}
            >
              <Activity className="h-4 w-4" />
              <span>Last</span>
              <span className="tabular-nums">{serverLoad?.loadPercent ?? "..." }%</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="space-y-1 text-left">
            {serverLoad ? (
              <>
                <div>CPU-Load: {serverLoad.loadPercent}% ({serverLoad.load1M} / {serverLoad.cpuCount} Kerne)</div>
                <div>RAM: {serverLoad.memoryPercent ?? "-"}%</div>
                <div>Disk: {serverLoad.diskPercent ?? "-"}%</div>
                <div>5/15 Min.: {serverLoad.load5M} / {serverLoad.load15M}</div>
              </>
            ) : (
              <div>Serverlast wird geladen...</div>
            )}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Right: Notifications & User */}
      <div className="flex items-center gap-2">
        {/* App-Switcher (externe Anwendungen, z. B. nxtools) */}
        <AppSwitcher />

        {/* Home */}
        <Button variant="ghost" size="icon" onClick={() => navigate("/hr/my")} title="Mein Arbeitsplatz">
          <Home className="h-5 w-5" />
        </Button>

        {/* Help */}
        <HelpDrawer />

        <MeetCallsButton />

        {/* Notifications */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -right-1 -top-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </Badge>
              )}
              <span className="sr-only">Benachrichtigungen</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-96 p-0">
            <div className="flex items-center justify-between px-4 py-3">
              <h4 className="text-sm font-semibold">Benachrichtigungen</h4>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => markAllReadMutation.mutate()}
                  disabled={markAllReadMutation.isPending}
                >
                  <Check className="mr-1 h-3 w-3" />
                  Alle gelesen
                </Button>
              )}
            </div>
            <Separator />
            <ScrollArea className="max-h-80">
              {Array.isArray(notifications) && notifications.length > 0 ? (
                <div className="divide-y">
                  {notifications.map((n: { id: string; title: string; message?: string; createdAt?: string; isRead?: boolean }) => (
                    <button
                      key={n.id}
                      className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors ${
                        !n.isRead ? "bg-primary/5" : ""
                      }`}
                      onClick={() => {
                        if (!n.isRead) {
                          markReadMutation.mutate(n.id)
                        }
                      }}
                    >
                      <div className="flex items-start gap-2">
                        {!n.isRead && (
                          <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{n.title}</p>
                          {n.message && (
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                              {n.message}
                            </p>
                          )}
                          {n.createdAt && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {timeAgo(n.createdAt)}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-sm text-muted-foreground text-center">
                  Keine Benachrichtigungen
                </div>
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>

        {/* User Menu */}
        {isAuthenticated && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={userAvatar} alt={userName} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {userName}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {userEmail}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => setProfileDialogOpen(true)}
              >
                <User className="mr-2 h-4 w-4" />
                <span>Profil</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => navigate("/admin/settings?tab=system")}
              >
                <Settings className="mr-2 h-4 w-4" />
                <span>Einstellungen</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive cursor-pointer"
                onClick={() => logout()}
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Abmelden</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Profile Dialog */}
      <UserProfileDialog
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
      />
    </header>
  )
}
